import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import './MyLibrary.css';

// Storage bucket name - matches Firebase config storageBucket
// Format: https://storage.googleapis.com/{bucket}/userExports/{userId}/{exportId}.zip
const STORAGE_BUCKET = 'pixano-ff250.firebasestorage.app';

interface Submission {
  submissionId: string;
  taskId?: string;
  taskTitle?: string;
  category?: string;
  subcategory?: string;
  mediaUrl?: string;
  labels?: any[];
  labelCount?: number;
  isFullyLabeled?: boolean;
  status?: string;
  createdAt?: any;
  downloadCount?: number;
  lastExportId?: string;
  firstDownloadedAt?: any;
  lastDownloadedAt?: any;
}

export default function MyLibrary() {
  const { colors } = useTheme();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [selectedTask, setSelectedTask] = useState<string>('all');

  useEffect(() => {
    loadLibrarySubmissions();
  }, []);

  const loadLibrarySubmissions = async () => {
    if (!auth.currentUser) return;

    setLoading(true);
    try {
      // Get all downloaded submissions from userDownloads collection
      const downloadHistoryRef = collection(db, 'userDownloads', auth.currentUser.uid, 'items');
      const downloadHistorySnap = await getDocs(downloadHistoryRef);
      
      if (downloadHistorySnap.empty) {
        setSubmissions([]);
        setLoading(false);
        return;
      }

      // Create a map of submission IDs to download info
      const downloadMap = new Map<string, any>();
      downloadHistorySnap.forEach((doc) => {
        const data = doc.data();
        downloadMap.set(doc.id, {
          downloadCount: data.downloadCount || 0,
          firstDownloadedAt: data.firstDownloadedAt,
          lastDownloadedAt: data.lastDownloadedAt,
          lastExportId: data.lastExportId
        });
      });

      console.log('Found downloaded submissions:', downloadMap.size);

      // Get user's completedTasks to get task info
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const completedTasks = userData?.completedTasks || {};

      // Get root submissions with labels
      const submissionsRef = collection(db, 'submissions');
      const userSubmissionsQuery = query(
        submissionsRef,
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'completed')
      );
      const userSubmissionsSnap = await getDocs(userSubmissionsQuery);
      
      // Create a map of root submissions
      const rootSubmissionsMap = new Map<string, any>();
      userSubmissionsSnap.forEach((doc) => {
        const subData = doc.data();
        const labels = subData.labels;
        const hasLabels = labels && 
                         Array.isArray(labels) && 
                         labels.length > 0 &&
                         labels.some((label: any) => label !== null && label !== undefined);
        
        if (hasLabels) {
          rootSubmissionsMap.set(doc.id, {
            id: doc.id,
            submissionId: doc.id,
            ...subData
          });
        }
      });

      // Match downloaded submissions with root submissions and completedTasks
      const librarySubmissions: Submission[] = [];
      
      // Iterate through downloaded submissions
      downloadMap.forEach((downloadInfo, submissionId) => {
        // Try to find in root submissions
        let rootSubmission = rootSubmissionsMap.get(submissionId);
        
        // If not found by ID, try to find in completedTasks and match by mediaUrl
        if (!rootSubmission) {
          // Search in completedTasks
          for (const [taskId, taskData] of Object.entries(completedTasks)) {
            const taskSubmissions = Array.isArray((taskData as any).submissions) 
              ? (taskData as any).submissions 
              : [taskData];
            
            for (const sub of taskSubmissions) {
              const subId = sub.submissionId || sub.id;
              if (subId === submissionId) {
                // Found in completedTasks, try to match with root by mediaUrl
                if (sub.mediaUrl) {
                  const rootSubmissionsArray = Array.from(rootSubmissionsMap.entries());
                  for (let i = 0; i < rootSubmissionsArray.length; i++) {
                    const [rootId, rootSub] = rootSubmissionsArray[i];
                    if (rootSub.mediaUrl === sub.mediaUrl) {
                      rootSubmission = rootSub;
                      break;
                    }
                  }
                }
                break;
              }
            }
            if (rootSubmission) break;
          }
        }

        // If we found a root submission, use it; otherwise try to construct from completedTasks
        if (rootSubmission) {
          // Find matching task info from completedTasks
          let taskInfo: any = null;
          for (const [taskId, taskData] of Object.entries(completedTasks)) {
            const taskSubmissions = Array.isArray((taskData as any).submissions) 
              ? (taskData as any).submissions 
              : [taskData];
            
            const matchingSub = taskSubmissions.find((sub: any) => {
              const subId = sub.submissionId || sub.id;
              return subId === submissionId || 
                     (sub.mediaUrl && rootSubmission.mediaUrl === sub.mediaUrl);
            });
            
            if (matchingSub) {
              taskInfo = { taskId, taskData, sub: matchingSub };
              break;
            }
          }

          const labels = rootSubmission.labels || [];
          const labelCount = labels.length;
          
          librarySubmissions.push({
            submissionId: rootSubmission.id || submissionId,
            taskId: taskInfo?.taskId,
            taskTitle: taskInfo?.sub?.taskTitle || taskInfo?.taskData?.taskTitle || rootSubmission.taskTitle,
            category: taskInfo?.sub?.category || taskInfo?.taskData?.category || rootSubmission.category || 'N/A',
            subcategory: taskInfo?.sub?.subcategory || taskInfo?.taskData?.subcategory || rootSubmission.subcategory || 'N/A',
            mediaUrl: rootSubmission.mediaUrl || taskInfo?.sub?.mediaUrl,
            labels: labels,
            labelCount: labelCount,
            isFullyLabeled: true,
            status: 'completed',
            createdAt: rootSubmission.createdAt || taskInfo?.sub?.lastSubmittedAt || taskInfo?.sub?.createdAt,
            downloadCount: downloadInfo.downloadCount,
            lastExportId: downloadInfo.lastExportId,
            firstDownloadedAt: downloadInfo.firstDownloadedAt,
            lastDownloadedAt: downloadInfo.lastDownloadedAt
          });
        } else {
          // Fallback: try to get info from completedTasks only
          for (const [taskId, taskData] of Object.entries(completedTasks)) {
            const taskSubmissions = Array.isArray((taskData as any).submissions) 
              ? (taskData as any).submissions 
              : [taskData];
            
            const matchingSub = taskSubmissions.find((sub: any) => {
              const subId = sub.submissionId || sub.id;
              return subId === submissionId;
            });
            
            if (matchingSub && matchingSub.status === 'approved') {
              librarySubmissions.push({
                submissionId: submissionId,
                taskId: taskId,
                taskTitle: matchingSub.taskTitle || (taskData as any).taskTitle,
                category: matchingSub.category || (taskData as any).category || 'N/A',
                subcategory: matchingSub.subcategory || (taskData as any).subcategory || 'N/A',
                mediaUrl: matchingSub.mediaUrl,
                labels: [],
                labelCount: 0,
                isFullyLabeled: false,
                status: 'approved',
                createdAt: matchingSub.lastSubmittedAt || matchingSub.createdAt,
                downloadCount: downloadInfo.downloadCount,
                lastExportId: downloadInfo.lastExportId,
                firstDownloadedAt: downloadInfo.firstDownloadedAt,
                lastDownloadedAt: downloadInfo.lastDownloadedAt
              });
              break;
            }
          }
        }
      });

      console.log('Library submissions loaded:', librarySubmissions.length);
      setSubmissions(librarySubmissions);
    } catch (error) {
      console.error('Error loading library submissions:', error);
      setError('Failed to load library submissions');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories and subcategories
  const getUniqueCategories = () => {
    const categories = new Set<string>();
    submissions.forEach(s => {
      if (s.category && s.category !== 'N/A') {
        categories.add(s.category);
      }
    });
    return Array.from(categories).sort();
  };
  
  const getUniqueSubcategories = () => {
    const subcategories = new Set<string>();
    submissions.forEach(s => {
      if (s.subcategory && s.subcategory !== 'N/A') {
        subcategories.add(s.subcategory);
      }
    });
    return Array.from(subcategories).sort();
  };

  // Filter submissions based on search and filters
  const getFilteredSubmissions = () => {
    return submissions.filter(sub => {
      // Category filter
      if (selectedCategory !== 'all' && sub.category !== selectedCategory) {
        return false;
      }
      
      // Subcategory filter
      if (selectedSubcategory !== 'all' && sub.subcategory !== selectedSubcategory) {
        return false;
      }
      
      // General search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const taskTitle = (sub.taskTitle || '').toLowerCase();
        const taskId = (sub.taskId || '').toLowerCase();
        const category = (sub.category || '').toLowerCase();
        const subcategory = (sub.subcategory || '').toLowerCase();
        
        if (!taskTitle.includes(query) && 
            !taskId.includes(query) && 
            !category.includes(query) && 
            !subcategory.includes(query)) {
          return false;
        }
      }
      
      // Task filter
      if (selectedTask !== 'all' && sub.taskId !== selectedTask) {
        return false;
      }
      
      return true;
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || 
           selectedCategory !== 'all' || 
           selectedSubcategory !== 'all' || 
           selectedTask !== 'all';
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setSelectedTask('all');
  };

  // Get unique tasks (filtered by category and subcategory if selected)
  const getUniqueTasks = () => {
    const taskMap = new Map<string, { title: string; id: string }>();
    submissions.forEach(s => {
      // Filter by category if selected
      if (selectedCategory !== 'all' && s.category !== selectedCategory) {
        return;
      }
      // Filter by subcategory if selected
      if (selectedSubcategory !== 'all' && s.subcategory !== selectedSubcategory) {
        return;
      }
      if (s.taskId && s.taskTitle) {
        taskMap.set(s.taskId, { title: s.taskTitle, id: s.taskId });
      }
    });
    return Array.from(taskMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  };

  const toggleSelection = (submissionId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(submissionId)) {
      newSelected.delete(submissionId);
    } else {
      newSelected.add(submissionId);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    const filtered = getFilteredSubmissions();
    const filteredIds = new Set(filtered.map(s => s.submissionId));
    
    if (filteredIds.size > 0 && Array.from(filteredIds).every(id => selectedIds.has(id))) {
      // Deselect all filtered items
      const newSelected = new Set(selectedIds);
      filteredIds.forEach(id => newSelected.delete(id));
      setSelectedIds(newSelected);
    } else {
      // Select all filtered items
      const newSelected = new Set(selectedIds);
      filteredIds.forEach(id => newSelected.add(id));
      setSelectedIds(newSelected);
    }
  };

  const handleDownload = () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one submission to download');
      return;
    }

    if (!auth.currentUser) {
      setError('You must be logged in to download');
      return;
    }

    // Get selected submissions
    const selected = submissions.filter(s => selectedIds.has(s.submissionId));
    
    // Group by exportId and download each unique export
    const exportIds = new Set<string>();
    selected.forEach(sub => {
      if (sub.lastExportId) {
        exportIds.add(sub.lastExportId);
      }
    });

    if (exportIds.size === 0) {
      setError('No download available for selected submissions');
      return;
    }

    // Download each unique export
    exportIds.forEach(exportId => {
      const downloadUrl = `https://storage.googleapis.com/${STORAGE_BUCKET}/userExports/${auth.currentUser!.uid}/${exportId}.zip`;
      window.open(downloadUrl, '_blank');
    });

    // Clear error on success
    setError(null);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString();
      }
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
      }
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleDateString();
      }
      return 'N/A';
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="library-page" style={{ background: colors.background, color: colors.text }}>
        <div className="library-container">
          <div className="loading">Loading your library...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="library-page" style={{ background: colors.background, color: colors.text }}>
      <div className="library-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: colors.text, margin: 0 }}>My Library</h1>
            <p className="library-description" style={{ color: colors.secondary, margin: '10px 0 0 0' }}>
              View and download your purchased submissions. These are the items you've downloaded using PIX.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: '20px' }}>
            <button
              onClick={selectAll}
              className="select-all-btn-header"
              style={{
                background: colors.card,
                borderColor: colors.border,
                color: colors.text,
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid',
                fontSize: '14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: '500'
              }}
              title="Select or deselect all visible items"
            >
              {(() => {
                const filtered = getFilteredSubmissions();
                const allFilteredSelected = filtered.length > 0 && 
                  filtered.every(s => selectedIds.has(s.submissionId));
                return allFilteredSelected ? 'Deselect All' : 'Select All';
              })()}
            </button>
            <button
              onClick={handleDownload}
              disabled={selectedIds.size === 0}
              className="download-selected-btn"
              style={{
                background: selectedIds.size === 0 ? colors.border : colors.primary,
                color: '#fff',
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: '500',
                opacity: selectedIds.size === 0 ? 0.6 : 1
              }}
            >
              Download Selected ({selectedIds.size})
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ background: colors.error + '20', color: colors.error }}>
            {error}
          </div>
        )}

        {submissions.length === 0 ? (
          <div className="empty-state" style={{ color: colors.secondary }}>
            No purchased submissions found. Download submissions from the "Download My Data" page to add them to your library.
          </div>
        ) : (
          <>
            {/* Filters and Search */}
            <div className="filters-section" style={{ marginBottom: '20px' }}>
              <div className="filters-row">
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search by task name, ID, category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      width: '100%',
                      maxWidth: '300px'
                    }}
                  />
                </div>
                
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedSubcategory('all');
                      setSelectedTask('all');
                    }}
                    className="filter-select"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All Categories</option>
                    {getUniqueCategories().map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Subcategory
                  </label>
                  <select
                    value={selectedSubcategory}
                    onChange={(e) => {
                      setSelectedSubcategory(e.target.value);
                      setSelectedTask('all');
                    }}
                    className="filter-select"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                    disabled={selectedCategory === 'all'}
                  >
                    <option value="all">All Subcategories</option>
                    {getUniqueSubcategories()
                      .filter(sub => selectedCategory === 'all' || 
                        submissions.some(s => s.category === selectedCategory && s.subcategory === sub))
                      .map(sub => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Task
                  </label>
                  <select
                    value={selectedTask}
                    onChange={(e) => setSelectedTask(e.target.value)}
                    className="filter-select"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      cursor: 'pointer',
                      minWidth: '200px'
                    }}
                    disabled={selectedCategory === 'all' && selectedSubcategory === 'all'}
                  >
                    <option value="all">All Tasks</option>
                    {getUniqueTasks().map(task => (
                      <option key={task.id} value={task.id}>{task.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Filter Indicators */}
              {hasActiveFilters() && (
                <div className="filter-indicators" style={{ 
                  marginTop: '12px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ color: colors.secondary, fontSize: '12px', fontWeight: '500' }}>
                    Active Filters:
                  </span>
                  
                  {searchQuery.trim() && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Search: "{searchQuery}"
                      <button
                        onClick={() => setSearchQuery('')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  {selectedCategory !== 'all' && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Category: {selectedCategory}
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setSelectedSubcategory('all');
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  {selectedSubcategory !== 'all' && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Subcategory: {selectedSubcategory}
                      <button
                        onClick={() => setSelectedSubcategory('all')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  {selectedTask !== 'all' && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Task: {getUniqueTasks().find(t => t.id === selectedTask)?.title || selectedTask}
                      <button
                        onClick={() => setSelectedTask('all')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  <button
                    onClick={clearAllFilters}
                    className="clear-filters-btn"
                    style={{
                      background: 'transparent',
                      borderColor: colors.border,
                      color: colors.secondary,
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      marginLeft: '8px'
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>

            <div className="submissions-table">
              <table>
                <thead>
                  <tr style={{ background: colors.card, borderColor: colors.border }}>
                    <th style={{ borderColor: colors.border }}>Select</th>
                    <th style={{ borderColor: colors.border }}>Thumbnail</th>
                    <th style={{ borderColor: colors.border }}>Category</th>
                    <th style={{ borderColor: colors.border }}>Subcategory</th>
                    <th style={{ borderColor: colors.border }}>Task</th>
                    <th style={{ borderColor: colors.border }}>Labels</th>
                    <th style={{ borderColor: colors.border }}>Purchased</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredSubmissions().map((sub) => {
                    const isSelected = selectedIds.has(sub.submissionId);
                    return (
                      <tr
                        key={sub.submissionId}
                        style={{
                          background: isSelected ? colors.primary + '10' : colors.card,
                          borderColor: colors.border
                        }}
                      >
                        <td style={{ borderColor: colors.border }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(sub.submissionId)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.mediaUrl ? (
                            <img
                              src={sub.mediaUrl}
                              alt="Submission"
                              className="submission-thumbnail"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="no-thumbnail" style={{ background: colors.border }}>
                              No Image
                            </div>
                          )}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.category && sub.category !== 'N/A' ? sub.category : '-'}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.subcategory && sub.subcategory !== 'N/A' ? sub.subcategory : '-'}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          <div className="task-info">
                            <div className="task-title">{sub.taskTitle || 'Unknown Task'}</div>
                            <div className="task-id" style={{ color: colors.secondary, fontSize: '12px' }}>
                              {sub.taskId}
                            </div>
                          </div>
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.labelCount || 0}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {formatDate(sub.firstDownloadedAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

